import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { TemplatePicker } from "@/components/email/TemplatePicker";
import {
  createInvoice,
  listInvoices,
  renderInvoiceHtml,
  updateInvoiceStatus,
  updateInvoice,
  type Invoice,
  type InvoiceLineInput,
} from "@/api/invoices";
import { sendEmail } from "@/api/email";
import { useToast } from "@/components/ToastProvider";
import { useEmailTemplates } from "@/hooks/useEmailTemplates";
import type { EmailTemplateOption } from "@/types/email";
import { SectionError, SkeletonRows } from "@/components/ui/error-ui";

const formatAmount = (value: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(value);

const defaultLine = (): (InvoiceLineInput & { id: string }) => ({
  id: Math.random().toString(36).slice(2),
  description: "",
  quantity: 1,
  unit_price: 0,
});

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "open", label: "Open" },
  { value: "paid", label: "Paid" },
  { value: "void", label: "Void" },
];

const statusClass = (status: string) => {
  switch (status) {
    case "paid":
      return "bg-success/20 text-success border border-success/40";
    case "void":
      return "bg-muted/50 text-foreground/80 border border-border/60";
    case "draft":
      return "bg-gray-500/20 text-foreground/80 border border-gray-400/40";
    default:
      return "bg-warning/20 text-warning border border-warning/40";
  }
};

export function InvoicePanel() {
  const { push } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    issued_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    currency: "USD",
    notes: "",
  });
  const [lines, setLines] = useState<Array<InvoiceLineInput & { id: string }>>([defaultLine()]);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Invoice | null>(null);
  const [editForm, setEditForm] = useState({
    customer_name: "",
    customer_email: "",
    issued_date: "",
    due_date: "",
    currency: "USD",
    notes: "",
  });
  const [editLines, setEditLines] = useState<Array<InvoiceLineInput & { id: string }>>([]);
  const [editStatus, setEditStatus] = useState<string>("draft");
  const [savingEdit, setSavingEdit] = useState(false);
  const { templates: emailTemplates, loading: templatesLoading } = useEmailTemplates();
  const [emailTarget, setEmailTarget] = useState<Invoice | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const selectedTemplate = useMemo(
    () => emailTemplates.find((template) => template.id === selectedTemplateId) ?? null,
    [emailTemplates, selectedTemplateId],
  );

  useEffect(() => {
    void loadInvoices();
  }, []);

  const total = useMemo(() => {
    return lines.reduce((acc, line) => acc + Number(line.quantity || 0) * Number(line.unit_price || 0), 0);
  }, [lines]);

  const loadInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listInvoices();
      setInvoices(payload);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load invoices";
      setError(msg);
      push({ title: "Failed to load invoices", message: msg });
    } finally {
      setLoading(false);
    }
  };

  const updateLine = (id: string, patch: Partial<InvoiceLineInput>) => {
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const addLine = () => setLines((prev) => [...prev, defaultLine()]);

  const removeLine = (id: string) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((line) => line.id !== id)));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const materializedLines = lines.filter((line) => line.description.trim());
    if (!materializedLines.length) {
      push({ title: "Missing lines", message: "Add at least one line item" });
      return;
    }
    setSaving(true);
    try {
      await createInvoice({
        customer_name: form.customer_name,
        customer_email: form.customer_email || undefined,
        issued_date: form.issued_date,
        due_date: form.due_date || undefined,
        currency: form.currency,
        notes: form.notes || undefined,
        status: "open",
        lines: materializedLines.map(({ description, quantity, unit_price }) => ({ description, quantity, unit_price })),
      });
      push({ title: "Invoice created", message: form.customer_name || "" });
      setForm((prev) => ({ ...prev, customer_name: "", customer_email: "", notes: "" }));
      setLines([defaultLine()]);
      await loadInvoices();
    } catch (err) {
      push({ title: "Creation failed", message: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (invoiceId: string) => {
    try {
      const html = await renderInvoiceHtml(invoiceId);
      const previewWindow = window.open("", "invoice-preview");
      if (!previewWindow) {
        push({ title: "Popup blocked", message: "Allow popups to preview invoices" });
        return;
      }
      previewWindow.document.write(html);
      previewWindow.document.close();
    } catch (err) {
      push({ title: "Preview failed", message: err instanceof Error ? err.message : "Unknown error" });
    }
  };

  const handleOpenEdit = (invoice: Invoice) => {
    setEditTarget(invoice);
    setEditForm({
      customer_name: invoice.customer_name,
      customer_email: invoice.customer_email ?? "",
      issued_date: invoice.issued_date.slice(0, 10),
      due_date: invoice.due_date ? invoice.due_date.slice(0, 10) : "",
      currency: invoice.currency,
      notes: invoice.notes ?? "",
    });
    setEditLines(
      (invoice.lines ?? []).map((line) => ({
        id: Math.random().toString(36).slice(2),
        description: line.description,
        quantity: Number(line.quantity) || 0,
        unit_price: Number(line.unit_price) || 0,
      })),
    );
    setEditStatus(invoice.status);
  };

  const handleCloseEdit = () => {
    setEditTarget(null);
    setEditLines([]);
    setEditStatus("draft");
  };

  const handleStatusChange = async (invoiceId: string, status: string) => {
    setUpdatingId(invoiceId);
    try {
      await updateInvoiceStatus(invoiceId, status);
      await loadInvoices();
      push({ title: "Invoice updated", message: `Status set to ${status}` });
    } catch (err) {
      push({ title: "Update failed", message: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setUpdatingId(null);
    }
  };

  const updateEditLine = (id: string, patch: Partial<InvoiceLineInput>) => {
    setEditLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const addEditLine = () => setEditLines((prev) => [...prev, defaultLine()]);

  const removeEditLine = (id: string) => {
    setEditLines((prev) => (prev.length <= 1 ? prev : prev.filter((line) => line.id !== id)));
  };

  const handleEditSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editTarget) return;
    const materializedLines = editLines.filter((line) => line.description.trim());
    if (!materializedLines.length) {
      push({ title: "Missing lines", message: "Add at least one line item" });
      return;
    }
    setSavingEdit(true);
    try {
      await updateInvoice(editTarget.id, {
        customer_name: editForm.customer_name,
        customer_email: editForm.customer_email || undefined,
        issued_date: editForm.issued_date,
        due_date: editForm.due_date || undefined,
        currency: editForm.currency,
        notes: editForm.notes || undefined,
        status: editStatus,
        lines: materializedLines.map(({ description, quantity, unit_price }) => ({ description, quantity, unit_price })),
      });
      push({ title: "Invoice updated", message: editForm.customer_name });
      handleCloseEdit();
      await loadInvoices();
    } catch (err) {
      push({ title: "Update failed", message: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setSavingEdit(false);
    }
  };

  const buildDefaultEmailBody = (invoice: Invoice) => {
    const amount = formatAmount(Number(invoice.total ?? invoice.subtotal ?? 0), invoice.currency);
    return `Hi ${invoice.customer_name},

Please find invoice ${invoice.invoice_number} totaling ${amount}. Let us know if you need an updated W9 or prefer ACH instead of check.

Thanks,
Americana Agents`;
  };

  const handleOpenEmail = (invoice: Invoice) => {
    if (!invoice.customer_email) {
      push({ title: "Missing email", message: "Add a customer email before sending invoices." });
      return;
    }
    setEmailTarget(invoice);
    setEmailSubject(`Invoice ${invoice.invoice_number}`);
    setEmailBody(buildDefaultEmailBody(invoice));
    setSelectedTemplateId(null);
  };

  const handleCloseEmailDialog = () => {
    setEmailTarget(null);
    setEmailSubject("");
    setEmailBody("");
    setSelectedTemplateId(null);
  };

  const handleTemplateSelect = (template: EmailTemplateOption | null) => {
    setSelectedTemplateId(template?.id ?? null);
    if (template) {
      setEmailSubject(template.subject);
      setEmailBody(template.body_html);
    }
  };

  const handleSendInvoiceEmail = async () => {
    if (!emailTarget || !emailTarget.customer_email) {
      push({ title: "Missing invoice", message: "Select an invoice before sending email." });
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      push({ title: "Missing content", message: "Add a subject and body before sending." });
      return;
    }
    setSendingEmail(true);
    try {
      await sendEmail({
        to: emailTarget.customer_email,
        template_id: selectedTemplate ? String(selectedTemplate.template_id) : undefined,
        subject: emailSubject.trim(),
        body_html: emailBody,
        context: {
          invoice_id: emailTarget.id,
          invoice_number: emailTarget.invoice_number,
        },
      });
      push({ title: "Invoice emailed", message: `Sent to ${emailTarget.customer_email}` });
      handleCloseEmailDialog();
    } catch (err) {
      push({ title: "Send failed", message: err instanceof Error ? err.message : "Unable to send invoice email." });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-background/70 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-primary-foreground dark:text-amber-100">Invoices</h2>
          <p className="text-sm text-muted-foreground">Create drafts rendered with the Jinja invoice template.</p>
        </div>
        <Button variant="secondary" onClick={loadInvoices} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-border bg-background/70 p-5">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Customer name
            <input
              type="text"
              value={form.customer_name}
              onChange={(event) => setForm((prev) => ({ ...prev, customer_name: event.target.value }))}
              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              required
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Customer email
            <input
              type="email"
              value={form.customer_email}
              onChange={(event) => setForm((prev) => ({ ...prev, customer_email: event.target.value }))}
              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Issued date
            <input
              type="date"
              value={form.issued_date}
              onChange={(event) => setForm((prev) => ({ ...prev, issued_date: event.target.value }))}
              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              required
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Due date
            <input
              type="date"
              value={form.due_date}
              onChange={(event) => setForm((prev) => ({ ...prev, due_date: event.target.value }))}
              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Currency
            <input
              type="text"
              value={form.currency}
              onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))}
              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Notes
            <input
              type="text"
              value={form.notes}
              onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
            />
          </label>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Line items</p>
            <Button type="button" variant="outline" size="sm" onClick={addLine}>
              Add line
            </Button>
          </div>
          {lines.map((line) => (
            <div key={line.id} className="grid gap-3 rounded-xl border border-border bg-background/30 p-3 text-sm md:grid-cols-4">
              <input
                type="text"
                value={line.description}
                onChange={(event) => updateLine(line.id, { description: event.target.value })}
                placeholder="Line description"
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground md:col-span-2"
              />
              <input
                type="number"
                value={line.quantity}
                min={0}
                onChange={(event) => updateLine(line.id, { quantity: Number(event.target.value) })}
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground"
              />
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={line.unit_price}
                  onChange={(event) => updateLine(line.id, { unit_price: Number(event.target.value) })}
                  className="w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground"
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(line.id)}>
                  ✕
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between text-sm text-foreground/70">
          <span>Total</span>
          <span className="font-semibold text-primary-foreground dark:text-amber-200">{formatAmount(total, form.currency || "USD")}</span>
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Create invoice"}
        </Button>
      </form>

      <div className="space-y-3 rounded-2xl border border-border bg-background/70 p-5">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Existing invoices</h2>
            <p className="text-xs text-muted-foreground">Preview or update statuses.</p>
          </div>
        </header>
        {error && <SectionError message={error} onRetry={() => void loadInvoices()} />}
        {!error && loading && invoices.length === 0 && <SkeletonRows rows={4} />}
        {!error && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-background/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Invoice</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="border-t border-border">
                  <td className="px-3 py-2 text-foreground/80">{invoice.invoice_number ?? invoice.id}</td>
                  <td className="px-3 py-2 text-muted-foreground">{invoice.customer_name}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase ${statusClass(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-primary-foreground dark:text-amber-100">
                    {formatAmount(
                      Number(typeof invoice.total === "number" ? invoice.total : invoice.subtotal ?? 0),
                      invoice.currency || "USD",
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-primary/60 text-primary hover:bg-primary/10 disabled:text-muted-foreground"
                        onClick={() => handleOpenEmail(invoice)}
                        disabled={!invoice.customer_email}
                      >
                        Email
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(invoice)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => handlePreview(invoice.id)}>
                        Preview
                      </Button>
                      <select
                        className="rounded-lg border border-border bg-background/40 px-2 py-1 text-xs text-foreground/80"
                        value={invoice.status}
                        onChange={(event) => handleStatusChange(invoice.id, event.target.value)}
                        disabled={updatingId === invoice.id}
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">
                    No invoices yet. Create one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {emailTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-border bg-background p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Invoice email</p>
                <h3 className="text-xl font-semibold text-foreground">{emailTarget.invoice_number}</h3>
                <p className="text-xs text-muted-foreground">{emailTarget.customer_email}</p>
              </div>
              <button
                type="button"
                onClick={handleCloseEmailDialog}
                className="rounded-full border border-border px-3 py-2 min-h-[44px] md:min-h-[36px] text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
              >
                Close
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-[1.5fr,1fr]">
              <div className="space-y-3">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Subject
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(event) => setEmailSubject(event.target.value)}
                    className="mt-2 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  />
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Body (HTML supported)
                  <textarea
                    value={emailBody}
                    onChange={(event) => setEmailBody(event.target.value)}
                    rows={10}
                    className="mt-2 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  />
                </label>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Templates</p>
                  {selectedTemplate && (
                    <button
                      type="button"
                      className="inline-flex min-h-[44px] md:min-h-[36px] items-center px-2 text-[11px] text-muted-foreground hover:text-primary-foreground dark:text-amber-200"
                      onClick={() => handleTemplateSelect(null)}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <TemplatePicker
                  templates={emailTemplates}
                  loading={templatesLoading}
                  selectedId={selectedTemplateId}
                  onSelect={handleTemplateSelect}
                />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button variant="ghost" onClick={handleCloseEmailDialog}>
                Cancel
              </Button>
              <Button onClick={handleSendInvoiceEmail} disabled={sendingEmail}>
                {sendingEmail ? "Sending…" : "Send email"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {editTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-5xl rounded-2xl border border-border bg-background p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Edit invoice</p>
                <h3 className="text-xl font-semibold text-foreground">{editTarget.invoice_number}</h3>
              </div>
              <button
                type="button"
                onClick={handleCloseEdit}
                className="rounded-full border border-border px-3 py-2 min-h-[44px] md:min-h-[36px] text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
              >
                Close
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Customer name
                  <input
                    type="text"
                    value={editForm.customer_name}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, customer_name: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                    required
                  />
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Customer email
                  <input
                    type="email"
                    value={editForm.customer_email}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, customer_email: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  />
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Issued date
                  <input
                    type="date"
                    value={editForm.issued_date}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, issued_date: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                    required
                  />
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Due date
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, due_date: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  />
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Currency
                  <input
                    type="text"
                    value={editForm.currency}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  />
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Status
                  <select
                    value={editStatus}
                    onChange={(event) => setEditStatus(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs uppercase tracking-wide text-muted-foreground md:col-span-2">
                  Notes
                  <input
                    type="text"
                    value={editForm.notes}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  />
                </label>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Line items</p>
                  <Button type="button" variant="outline" size="sm" onClick={addEditLine}>
                    Add line
                  </Button>
                </div>
                {editLines.map((line) => (
                  <div key={line.id} className="grid gap-3 rounded-xl border border-border bg-background/30 p-3 text-sm md:grid-cols-4">
                    <input
                      type="text"
                      value={line.description}
                      onChange={(event) => updateEditLine(line.id, { description: event.target.value })}
                      placeholder="Line description"
                      className="rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground md:col-span-2"
                    />
                    <input
                      type="number"
                      value={line.quantity}
                      min={0}
                      onChange={(event) => updateEditLine(line.id, { quantity: Number(event.target.value) })}
                      className="rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        value={line.unit_price}
                        onChange={(event) => updateEditLine(line.id, { unit_price: Number(event.target.value) })}
                        className="w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground"
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeEditLine(line.id)}>
                        ✕
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end">
                <Button type="submit" disabled={savingEdit}>
                  {savingEdit ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
